import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { wishlist as wishlistApi, API_URL } from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Heart, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';

export const Wishlist = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadWishlist();
  }, [user]);

  const loadWishlist = async () => {
    try {
      const data = await wishlistApi.get();
      setItems(data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleRemove = async (pid) => {
    try {
      await wishlistApi.remove(pid);
      setItems(items.filter(i => i.id !== pid));
      toast.success('Removed from wishlist');
    } catch (e) { toast.error('Failed'); }
  };

  const handleAddToCart = async (product) => {
    const size = product.sizes?.[0] || 'M';
    try {
      await addToCart(product.id, size, 1);
      toast.success(`${product.name} added to cart`);
    } catch (e) { toast.error('Failed to add to cart'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#E2E2E2] border-t-[#C9A84C] rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F9F8F6] section-padding" data-testid="wishlist-page">
      <SEO title="My Wishlist" description="Your saved vintage pieces from Vintage Wardrobe Co." path="/wishlist" />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Heart className="w-6 h-6 text-[#C9A84C]" />
          <h1 className="text-3xl font-heading font-semibold" data-testid="wishlist-title">My Wishlist</h1>
          <span className="text-sm text-[#666]">({items.length} items)</span>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20" data-testid="wishlist-empty">
            <Heart className="w-12 h-12 text-[#E2E2E2] mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">Your wishlist is empty</h2>
            <p className="text-sm text-[#666] mb-6">Save your favourite vintage pieces for later</p>
            <Link to="/shop">
              <Button className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] px-8">
                <ShoppingBag className="w-4 h-4 mr-2" /> Browse Collection
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map(product => (
              <div key={product.id} className="bg-white rounded-xl border border-[#E2E2E2] overflow-hidden group hover:shadow-md transition-shadow" data-testid={`wishlist-item-${product.id}`}>
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-[3/4] bg-[#EFE5D6]/20 relative overflow-hidden">
                    <img
                      src={product.images?.[0] ? `${API_URL}${product.images[0]}` : 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {product.stock <= 2 && product.stock > 0 && (
                      <span className="absolute top-2 left-2 bg-[#B8733A] text-white text-[9px] uppercase font-bold px-2 py-0.5 rounded">Only {product.stock} left</span>
                    )}
                    {product.stock === 0 && (
                      <span className="absolute top-2 left-2 bg-red-500 text-white text-[9px] uppercase font-bold px-2 py-0.5 rounded">Sold Out</span>
                    )}
                  </div>
                </Link>
                <div className="p-3">
                  <p className="text-[9px] uppercase tracking-wider text-[#666]">{product.category}</p>
                  <Link to={`/products/${product.id}`}>
                    <h3 className="text-sm font-medium text-[#0A0A0A] mt-0.5 truncate hover:text-[#C9A84C] transition-colors">{product.name}</h3>
                  </Link>
                  <p className="text-base font-bold text-[#0A0A0A] mt-1">&pound;{product.price?.toFixed(2)}</p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      disabled={product.stock === 0}
                      className="flex-1 bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] h-8 text-[9px] uppercase tracking-wider font-bold"
                      data-testid={`wishlist-add-to-cart-${product.id}`}
                    >
                      <ShoppingBag className="w-3 h-3 mr-1" /> Add to Cart
                    </Button>
                    <Button
                      onClick={() => handleRemove(product.id)}
                      variant="outline"
                      className="h-8 w-8 p-0 border-[#E2E2E2] hover:border-red-300 hover:text-red-500"
                      data-testid={`wishlist-remove-${product.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
